
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Layout/Navbar';
import { isAuthenticated } from '../utils/auth';
import api from '../services/api';
import TimingForm from '../components/Admin/TimingForm';
import './Timings.css';

interface ApiDaySchedule {
  id: number;
  day_of_week: string;
  is_open: boolean;
  open_time: string | null;
  close_time: string | null;
  appointment_type: string;
  slot_duration: number;
}

// Frontend interface (matches your existing UI)
interface DaySchedule {
  id: number;
  day: string;
  hours: string;
  isClosed: boolean;
  open_time: string | null;
  close_time: string | null;
}

const Timings: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('Timings');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedDay, setSelectedDay] = useState<DaySchedule | null>(null);
  
  // Timing data - will be populated from API
  const [schedules, setSchedules] = useState<DaySchedule[]>([]);

  // Format time from backend (HH:MM:SS) to display format (e.g., "09:00 AM – 10:00 PM")
  const formatDisplayHours = (openTime: string | null, closeTime: string | null): string => {
    if (!openTime || !closeTime) return '';
    
    const formatSingleTime = (time: string): string => {
      const [hours, minutes] = time.split(':');
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const hour12 = hour % 12 || 12;
      // Always show leading zero for hours (e.g., 09 instead of 9)
      const hourStr = hour12.toString().padStart(2, '0');
      // Always show minutes with leading zero
      const minuteStr = minutes.padStart(2, '0');
      return `${hourStr}:${minuteStr} ${ampm}`;
    };
    
    return `${formatSingleTime(openTime)} – ${formatSingleTime(closeTime)}`;
  };

  // Convert display format to backend format
  const convertToBackendTime = (hoursStr: string): { open: string; close: string } | null => {
    // Parse format like "09:00 AM – 10:00 PM" or "09:00AM – 10:00PM"
    const match = hoursStr.match(/(\d+):(\d+)\s*(AM|PM)\s*[-–]\s*(\d+):(\d+)\s*(AM|PM)/i);
    if (!match) return null;
    
    const convertTime = (hour: number, minute: number, ampm: string): string => {
      let hour24 = hour;
      if (ampm.toUpperCase() === 'PM' && hour24 !== 12) hour24 += 12;
      if (ampm.toUpperCase() === 'AM' && hour24 === 12) hour24 = 0;
      const minuteStr = minute.toString().padStart(2, '0');
      return `${hour24.toString().padStart(2, '0')}:${minuteStr}:00`;
    };
    
    const startHour = parseInt(match[1]);
    const startMinute = parseInt(match[2]);
    const startAmpm = match[3];
    const endHour = parseInt(match[4]);
    const endMinute = parseInt(match[5]);
    const endAmpm = match[6];
    
    return {
      open: convertTime(startHour, startMinute, startAmpm),
      close: convertTime(endHour, endMinute, endAmpm)
    };
  };

  // Fetch working hours from API
  const fetchWorkingHours = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/working-hours');
      if (response.data.success && response.data.data) {
        const apiData: ApiDaySchedule[] = response.data.data;
        
        // Convert API data to frontend format
        const convertedSchedules: DaySchedule[] = apiData.map(item => ({
          id: item.id,
          day: item.day_of_week,
          hours: item.is_open ? formatDisplayHours(item.open_time, item.close_time) : '',
          isClosed: !item.is_open,
          open_time: item.open_time,
          close_time: item.close_time
        }));
        
        // Sort by day order
        const dayOrder = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
        convertedSchedules.sort((a, b) => dayOrder.indexOf(a.day) - dayOrder.indexOf(b.day));
        
        setSchedules(convertedSchedules);
      }
    } catch (error) {
      console.error('Error fetching working hours:', error);
    } finally {
      setLoading(false);
    }
  };

  // Save single day working hours to API
  const saveSingleDay = async (daySchedule: DaySchedule) => {
    setSaving(true);
    try {
      let openTime = null;
      let closeTime = null;
      let isOpen = !daySchedule.isClosed;
      
      if (isOpen && daySchedule.hours) {
        const converted = convertToBackendTime(daySchedule.hours);
        if (converted) {
          openTime = converted.open;
          closeTime = converted.close;
        }
      }
      
      const payload = {
        working_hours: [{
          id: daySchedule.id,
          day_of_week: daySchedule.day,
          is_open: isOpen,
          open_time: openTime,
          close_time: closeTime,
          appointment_type: 'all',
          slot_duration: 30
        }]
      };
      
      await api.put('/admin/working-hours', payload);
      await fetchWorkingHours();
    } catch (error) {
      console.error('Error saving working hours:', error);
    } finally {
      setSaving(false);
    }
  };

  // Load data on component mount
  useEffect(() => {
    fetchWorkingHours();
  }, []);

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    const routes: { [key: string]: string } = {
      'Dashboard': '/dashboard',
      'Calls': '/calls',
      'Conversations': '/conversations',
      'Transcriptions': '/transcriptions',
      'Timings': '/timings',
      'Doctors': '/doctors',
      'Pricing': '/pricing',
      'Appointments': '/appointments',
      'EmailConfig': '/emailconfig'
    };
    navigate(routes[tab] || '/dashboard');
  };

  const handleEdit = (index: number) => {
    setSelectedDay(schedules[index]);
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setSelectedDay(null);
  };

  const handleModalSubmit = async () => {
    setShowModal(false);
    setSelectedDay(null);
    await fetchWorkingHours();
  };

  const handleDelete = async (index: number) => {
    if (window.confirm(`Are you sure you want to close ${schedules[index].day}?`)) {
      const updatedSchedule = { ...schedules[index], hours: '', isClosed: true };
      await saveSingleDay(updatedSchedule);
      await fetchWorkingHours();
    }
  };

  if (!isAuthenticated()) return null;

  // Show loading state while fetching data
  if (loading) {
    return (
      <div className="timings-page">
        <Navbar activeTab={activeTab} onTabChange={handleTabChange} />
        <div className="page-content">
          <div className="page-header">
            <h1>🏥 Hospital Business Hours</h1>
          </div>
          <div style={{ textAlign: 'center', padding: '50px' }}>Loading working hours...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="timings-page">
      <Navbar activeTab={activeTab} onTabChange={handleTabChange} />
      <div className="page-content">
        <div className="page-header">
          <h1>🏥 Hospital Business Hours</h1>
          {saving && <span style={{ marginLeft: '15px', fontSize: '14px', color: '#667eea' }}>Saving...</span>}
        </div>

        <div className="timings-container">
          <div className="timings-header-row">
            <span className="header-day">Day</span>
            <span className="header-hours">Hours</span>
            <span className="header-status">Status</span>
            <span className="header-actions">Actions</span>
          </div>

          <div className="timings-list">
            {schedules.map((schedule, index) => (
              <div key={schedule.id} className={`timing-row ${schedule.isClosed ? 'closed-row' : ''}`}>
                {/* Display Mode */}
                <div className="timing-day">
                  <span className="day-name">{schedule.day}</span>
                </div>
                <div className="timing-hours-display">
                  {schedule.isClosed ? (
                    <span className="closed-text">—</span>
                  ) : (
                    <span className="hours-text">{schedule.hours}</span>
                  )}
                </div>
                <div className="timing-status">
                  <span className={`status-badge ${schedule.isClosed ? 'closed' : 'open'}`}>
                    {schedule.isClosed ? '🔴 Closed' : '🟢 Open'}
                  </span>
                </div>
                <div className="timing-actions">
                  <button 
                    className="edit-btn-small"
                    onClick={() => handleEdit(index)}
                    title="Edit"
                    disabled={saving}
                  >
                    ✏️
                  </button>
                  {!schedule.isClosed && (
                    <button 
                      className="delete-btn-small"
                      onClick={() => handleDelete(index)}
                      title="Mark as Closed"
                      disabled={saving}
                    >
                      🗑️
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Modal Form */}
      {showModal && selectedDay && (
        <TimingForm
          timing={{
            id: selectedDay.id,
            day_of_week: selectedDay.day,
            is_open: !selectedDay.isClosed,
            open_time: selectedDay.open_time,
            close_time: selectedDay.close_time,
            appointment_type: 'all',
            slot_duration: 30
          }}
          onClose={handleModalClose}
          onSubmit={handleModalSubmit}
        />
      )}
    </div>
  );
};

export default Timings;